// Calculator.jsx
import React, { useMemo, useState } from "react";

/** ---------- 통화 포맷 ---------- */
const KRW = new Intl.NumberFormat("ko-KR", {
  style: "currency",
  currency: "KRW",
  maximumFractionDigits: 0,
});

/** ---------- 기본 단가표(샘플) ----------
 * 실제 단가/품목코드는 회사 DB 값으로 치환하세요.
 */
const PRICE = {
  pipe: {
    GIP_25A: 2500, // 원/m
    GIP_32A: 3200,
    GIP_40A: 4200,
  },
  film: {
    PO_0_1: 1800, // 원/㎡ (두께 0.1)
    PO_0_15: 2400, // 원/㎡ (두께 0.15)
  },
  clip: 120, // 원/EA (필름 클립)
  wire: 900, // 원/m (결속/고정용 와이어)
  screw: 80, // 원/EA (피스로 고정)
  rollupKitPerMeter: 4500, // 원/m (측창 롤업 키트 (옵션))
};

/** ---------- 계산 파라미터(현장에 맞게 조정) ----------
 * frameSpacing: 아치 프레임 간격 (m)
 * purlinSpacing: 퍼린 간격 (m) → 지붕 폭 방향 라인 수 계산
 * roofFactor: 곡면 보정계수(지붕 면적) (평지붕=1.0, 터널형≈1.15~1.25)
 * wasteRate: 원단/파이프 여유율
 */
const DEFAULTS = {
  frameSpacing: 2.0, // m
  purlinSpacing: 1.5, // m
  roofFactor: 1.2,
  wasteRate: 0.05, // 5%
  clipsPerMeterArch: 2.0, // 아치 1m당 클립 개수(현장 조정)
  screwsPerFrame: 12, // 프레임당 피스
  wirePerimeterFactor: 1.05, // 둘레 고정용 와이어 여유
};

function clamp(n, min, max) {
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/** 2D 터널형 단순 근사: 아치 1개의 파이프 길이 ≈ 폭 + 2*높이 */
function archPipeLengthPerFrame(width, height) {
  return width + 2 * height;
}

/** 지붕/측면 피복 면적 근사 (길이*폭*roofFactor + 전후면 삼각형 2개 보정) */
function filmArea(length, width, height, roofFactor, wasteRate) {
  const roof = length * width * roofFactor;
  const gables = width * height; // 양쪽 합계 근사(삼각/곡면 오차 포함)
  const area = (roof + gables) * (1 + wasteRate);
  return area; // ㎡
}

function downloadCSV(rows) {
  const header = ["코드", "품목", "단위", "단가(원)", "수량", "소계(원)"];
  const lines = [
    header.join(","),
    ...rows.map((r) => [r.code, r.name, r.unit, r.unitPrice, r.qty, r.subtotal].join(",")),
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `greenhouse_quote_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Calculator() {
  // 입력 상태
  const [length, setLength] = useState(30); // m
  const [width, setWidth] = useState(8); // m
  const [height, setHeight] = useState(3); // m
  const [frameSpacing, setFrameSpacing] = useState(DEFAULTS.frameSpacing);
  const [purlinSpacing, setPurlinSpacing] = useState(DEFAULTS.purlinSpacing);

  const [pipeOpt, setPipeOpt] = useState("GIP_32A"); // "GIP_25A" | "GIP_32A" | "GIP_40A"
  const [filmOpt, setFilmOpt] = useState("PO_0_1"); // "PO_0_1" | "PO_0_15"
  const [useRollup, setUseRollup] = useState(true); // 측창 롤업(양측)

  // 입력 유효성
  const valid =
    length > 0 && width > 0 && height > 0 && frameSpacing > 0.5 && purlinSpacing > 0.5;

  // 계산
  const rows = useMemo(() => {
    if (!valid) return [];

    const frames = Math.floor(length / frameSpacing) + 1; // 프레임(아치) 개수
    const archLen = archPipeLengthPerFrame(width, height); // 프레임 1개당 파이프 m
    const pipeMeterTotal = frames * archLen * (1 + DEFAULTS.wasteRate);

    // 퍼린 라인 수(폭 방향) → 지붕 양쪽 포함을 위해 대략 (폭/간격)+1
    const purlinLines = Math.ceil(width / purlinSpacing) + 1;
    const purlinMeter = purlinLines * length * (1 + DEFAULTS.wasteRate);

    // 필름 면적
    const area = filmArea(length, width, height, DEFAULTS.roofFactor, DEFAULTS.wasteRate);

    // 클립/피스/와이어
    const clipQty = Math.ceil(archLen * frames * DEFAULTS.clipsPerMeterArch);
    const screwQty = Math.ceil(frames * DEFAULTS.screwsPerFrame);
    const wireMeter =
      Math.ceil(((archLen * frames) * DEFAULTS.wirePerimeterFactor) / 10) * 10; // 10m 단위 반올림

    // 롤업(측창): 양측 길이 합×1세트 가정
    const rollupMeter = useRollup ? length * 2 : 0;

    const pipePrice = PRICE.pipe[pipeOpt];
    const filmPrice = PRICE.film[filmOpt];

    const list = [
      {
        code: pipeOpt,
        name: `아치프레임 파이프 (${pipeOpt})`,
        unit: "m",
        unitPrice: pipePrice,
        qty: Math.round(pipeMeterTotal * 10) / 10,
        subtotal: Math.round(pipeMeterTotal * pipePrice),
      },
      {
        code: "PURLIN",
        name: "지붕 퍼린 파이프",
        unit: "m",
        unitPrice: pipePrice, // 같은 규격 사용 가정(필요시 분리)
        qty: Math.round(purlinMeter * 10) / 10,
        subtotal: Math.round(purlinMeter * pipePrice),
      },
      {
        code: filmOpt,
        name: `피복(PO 필름 ${filmOpt === "PO_0_1" ? "0.10mm" : "0.15mm"})`,
        unit: "㎡",
        unitPrice: filmPrice,
        qty: Math.round(area),
        subtotal: Math.round(area * filmPrice),
      },
      {
        code: "CLIP",
        name: "필름 클립",
        unit: "EA",
        unitPrice: PRICE.clip,
        qty: clipQty,
        subtotal: clipQty * PRICE.clip,
      },
      {
        code: "WIRE",
        name: "결속 와이어",
        unit: "m",
        unitPrice: PRICE.wire,
        qty: wireMeter,
        subtotal: wireMeter * PRICE.wire,
      },
      {
        code: "SCREW",
        name: "피스(고정용)",
        unit: "EA",
        unitPrice: PRICE.screw,
        qty: screwQty,
        subtotal: screwQty * PRICE.screw,
      },
    ];

    if (useRollup && rollupMeter > 0) {
      list.push({
        code: "ROLLUP",
        name: "측창 롤업 키트(양측)",
        unit: "m",
        unitPrice: PRICE.rollupKitPerMeter,
        qty: Math.round(rollupMeter),
        subtotal: Math.round(rollupMeter * PRICE.rollupKitPerMeter),
      });
    }

    return list;
  }, [valid, length, width, height, frameSpacing, purlinSpacing, pipeOpt, filmOpt, useRollup]);

  const total = rows.reduce((s, r) => s + r.subtotal, 0);

  function handleRequestQuote() {
    // TODO: Django로 POST (사용자 연락처/회사명/현장주소 + rows + 총액)
    alert("견적요청이 접수되었습니다. 담당자가 곧 연락드립니다. (데모)");
  }

  return (
    <div className="gh-wrap">
      <h2 className="gh-title">온실 자재 자동 산출 · 견적 계산기</h2>

      {/* 입력 폼 */}
      <section className="gh-card">
        <div className="gh-grid">
          <NumberField label="길이 (m)" value={length} onChange={setLength} min={2} />
          <NumberField label="폭 (m)" value={width} onChange={setWidth} min={3} />
          <NumberField label="높이 (m)" value={height} onChange={setHeight} min={2} />
          <NumberField
            label="프레임 간격 (m)"
            value={frameSpacing}
            onChange={setFrameSpacing}
            step={0.5}
            min={0.5}
          />
          <NumberField
            label="퍼린 간격 (m)"
            value={purlinSpacing}
            onChange={setPurlinSpacing}
            step={0.5}
            min={0.5}
          />
          <div className="gh-field">
            <label>프레임 파이프</label>
            <select value={pipeOpt} onChange={(e) => setPipeOpt(e.target.value)}>
              <option value="GIP_25A">GIP 25A</option>
              <option value="GIP_32A">GIP 32A</option>
              <option value="GIP_40A">GIP 40A</option>
            </select>
          </div>
          <div className="gh-field">
            <label>피복(필름)</label>
            <select value={filmOpt} onChange={(e) => setFilmOpt(e.target.value)}>
              <option value="PO_0_1">PO 0.10mm</option>
              <option value="PO_0_15">PO 0.15mm</option>
            </select>
          </div>
          <div className="gh-field gh-colspan2">
            <label className="gh-check">
              <input
                type="checkbox"
                checked={useRollup}
                onChange={(e) => setUseRollup(e.target.checked)}
              />
              측창 롤업(양측) 포함
            </label>
          </div>
        </div>
        {!valid && (
          <p className="gh-warn">입력값을 확인해주세요. (0보다 큰 값, 간격은 0.5m 이상)</p>
        )}
      </section>

      {/* 결과 표 */}
      <section className="gh-card">
        <div className="gh-toolbar">
          <button className="gh-btn" onClick={() => downloadCSV(rows)} disabled={!rows.length}>
            CSV로 내보내기
          </button>
          <button className="gh-btn gh-primary" onClick={handleRequestQuote} disabled={!rows.length}>
            견적 요청
          </button>
        </div>

        <div className="gh-table-wrap">
          <table className="gh-table">
            <thead>
              <tr>
                <th>코드</th>
                <th>품목</th>
                <th>단위</th>
                <th className="gh-right">단가</th>
                <th className="gh-right">수량</th>
                <th className="gh-right">소계</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={6} className="gh-center gh-muted">
                    유효한 입력을 먼저 설정해 주세요.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.code}>
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.unit}</td>
                  <td className="gh-right">{KRW.format(r.unitPrice)}</td>
                  <td className="gh-right">
                    {r.unit === "m" || r.unit === "㎡" ? r.qty.toLocaleString() : r.qty}
                  </td>
                  <td className="gh-right">{KRW.format(r.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="gh-right gh-bold">
                  총액
                </td>
                <td className="gh-right gh-bold">{KRW.format(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="gh-hint">
          ※ 계산식은 단순 근사이며, 현장 여건에 따라 ±5~10% 오차가 발생할 수 있습니다.
        </p>
      </section>

      {/* 컴포넌트 전용 스타일 */}
      <style>{`
        .gh-wrap { max-width: 960px; margin: 24px auto; padding: 0 16px; font-family: system-ui, -apple-system, Segoe UI, Roboto, 'Noto Sans KR', sans-serif; color:#111; }
        .gh-title { font-size: 20px; font-weight: 700; margin-bottom: 12px; }
        .gh-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; margin-bottom: 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .gh-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        .gh-field { display: flex; flex-direction: column; gap: 6px; }
        .gh-field input, .gh-field select { border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 14px; }
        .gh-check { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; }
        .gh-colspan2 { grid-column: span 2; }
        .gh-warn { margin-top: 10px; color: #b45309; font-size: 13px; }

        .gh-toolbar { display:flex; gap:8px; justify-content:flex-end; margin-bottom: 10px; }
        .gh-btn { border:1px solid #d1d5db; background:#fff; padding:8px 12px; border-radius:8px; font-size:14px; cursor:pointer; }
        .gh-btn:hover { background:#f9fafb; }
        .gh-btn:disabled { opacity:.5; cursor:not-allowed; }
        .gh-primary { background:#111827; color:#fff; border-color:#111827; }
        .gh-primary:hover { background:#0b1220; }

        .gh-table-wrap { overflow-x:auto; }
        .gh-table { width:100%; border-collapse: collapse; font-size:14px; }
        .gh-table th, .gh-table td { border-bottom:1px solid #e5e7eb; padding:10px; }
        .gh-right { text-align:right; }
        .gh-center { text-align:center; }
        .gh-muted { color:#6b7280; }
        .gh-bold { font-weight:700; }
        .gh-hint { color:#6b7280; font-size:12px; margin-top:8px; }

        @media (max-width: 640px) {
          .gh-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

/** 숫자 입력 공용 컴포넌트 (JSX 버전) */
function NumberField({ label, value, onChange, min = 0, step = 0.1 }) {
  return (
    <div className="gh-field">
      <label>{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => onChange(clamp(parseFloat(e.target.value), min, 9999))}
      />
    </div>
  );
}
